/* eslint-disable react/no-multi-comp */
/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import Fuse from 'fuse.js';
import { Box, DialogContentText, TableCell } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  ButtonMenuV2,
  DialogDefaultV2,
  DropzoneModalV2,
  TableWidgetV2,
} from '../../../components/v2';
import NsmpModal from './NsmpModal';
import UsoMutuoQuickFilters from './components/UsoMutuoQuickFilters';
import ReatribuidaDialog from './mtbt/components/ReatribuidaDialog';
import { useUsoMutuoSave } from './hooks/useUsoMutuoSave';
import { isDigitadorEmDigitacao } from './utils/servicosPermissions';
import { isAnaliseRiscoOrFotoRegistro, isUsoMutuoServiceRow, getUsoMutuoRowKind, USO_MUTUO_ROW_KIND } from './utils/nsmpRowTypes';
import {
  renderUsoMutuoItemCell,
  renderUsoMutuoSituacaoCell,
  renderUsoMutuoStatusCell,
  UsoMutuoCoordenadaCell,
} from './utils/usoMutuoRowDisplay';
import {
  applyUsoMutuoFilters,
  buildFiltroFromQuickFilter,
  EMPTY_USO_MUTUO_QUICK_FILTER,
  isUsoMutuoQuickFilterActive,
  sortUsoMutuoRows,
} from './utils/usoMutuoFilters';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as actionsNsmp from '../../../store/nsmp/actions';
import * as Status from '../../../constants/status';
import * as Role from '../../../constants/role';
import { useNsmpUrlSync } from './hooks/useNsmpUrlSync';
import { TIPO_NSMP_SUCATA } from './utils/usoMutuoSucataSchema';

const TABLE_TITLES = ['Item', 'Coordenada', 'Situação', 'Status'];

const UsoMutuoServiceTab = ({
  nsmps,
  nsirId,
  updateNsmp,
  saveNsmp,
  digitarNsmpUsoMutuo,
  cleanSelectedNsmp,
  nsirStatus,
  userRole,
  deleteNsmp,
  authData,
  userId,
  inspetorId,
  digitadorId,
  nsirTitle,
  cloneNsmp,
  deleteCloneNsmp,
  history,
  location,
  nsirData,
}) => {
  const [quickFilter, setQuickFilter] = useState(EMPTY_USO_MUTUO_QUICK_FILTER);
  const [open, setOpen] = useState(false);
  const [openFotoInicial, setOpenFotoInicial] = useState(false);
  const [openFotoFinal, setOpenFotoFinal] = useState(false);
  const [openAnaliseRisco, setOpenAnaliseRisco] = useState(false);
  const [openClone, setOpenClone] = useState(false);
  const [openDeleteClone, setOpenDeleteClone] = useState(false);
  const [cloneId, setCloneId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [createTipoNsmp, setCreateTipoNsmp] = useState(null);
  const [reatribuidaDialog, setReatribuidaDialog] = useState({ open: false, message: '' });

  const sortedNsmps = useMemo(() => sortUsoMutuoRows(nsmps), [nsmps]);

  const mergedFiltro = useMemo(
    () => buildFiltroFromQuickFilter(quickFilter),
    [quickFilter]
  );

  const fuse = useMemo(
    () => new Fuse(sortedNsmps, {
      keys: ['endereco'],
      threshold: 0.4,
      distance: 100,
      ignoreLocation: true,
      includeScore: true,
      ignoreDiacritics: true,
      isCaseSensitive: false,
    }),
    [sortedNsmps]
  );

  const filteredRows = useMemo(
    () => applyUsoMutuoFilters(sortedNsmps, mergedFiltro, fuse),
    [sortedNsmps, mergedFiltro, fuse]
  );

  const filterActive = isUsoMutuoQuickFilterActive(quickFilter);

  const isAdmin = () => authData && (authData.role === Role.ADMIN || authData.role === Role.SUPER_ADMIN);

  const handleNsmpNotInFilter = useCallback(() => {
    setQuickFilter(EMPTY_USO_MUTUO_QUICK_FILTER);
  }, []);

  const { openNsmpModal, closeNsmpModal, syncCarouselNsmpId } = useNsmpUrlSync({
    history,
    location,
    enabled: !!nsirId,
    allRows: nsmps,
    filteredRows,
    open,
    selectedId,
    setOpen,
    setSelectedId,
    setCurrentIndex,
    setReadOnly,
    cleanSelectedNsmp,
    onNsmpNotInFilter: handleNsmpNotInFilter,
  });

  const openCreateNsmp = (tipoNsmp = null) => {
    setCreateTipoNsmp(tipoNsmp);
    openNsmpModal({
      id: null,
      readOnly: false,
      index: null,
    });
  };

  const onEdit = (id) => {
    setCreateTipoNsmp(null);
    const index = filteredRows.findIndex((r) => r.id === id);
    openNsmpModal({
      id,
      readOnly: userRole === Role.DIGITADOR,
      index,
    });
  };

  const onView = (id) => {
    setCreateTipoNsmp(null);
    const index = filteredRows.findIndex((r) => r.id === id);
    openNsmpModal({
      id,
      readOnly: true,
      index,
    });
  };

  const onAddPoste = () => openCreateNsmp(null);
  const onAddEntrega = () => openCreateNsmp(TIPO_NSMP_SUCATA.ENTREGA);
  const onAddPesagem = () => openCreateNsmp(TIPO_NSMP_SUCATA.PESAGEM);

  const handlerCancel = () => {
    setCreateTipoNsmp(null);
    closeNsmpModal();
  };

  const goToNext = () => {
    if (currentIndex !== null && currentIndex < filteredRows.length - 1) {
      const next = filteredRows[currentIndex + 1];
      setCurrentIndex(currentIndex + 1);
      setSelectedId(next.id);
      syncCarouselNsmpId(next.id);
    }
  };

  const goToPrevious = () => {
    if (currentIndex !== null && currentIndex > 0) {
      const prev = filteredRows[currentIndex - 1];
      setCurrentIndex(currentIndex - 1);
      setSelectedId(prev.id);
      syncCarouselNsmpId(prev.id);
    }
  };

  const closeModalAfterSave = useCallback(
    (_result, { advanceNext } = {}) => {
      if (advanceNext) {
        goToNext();
      } else {
        setCreateTipoNsmp(null);
        closeNsmpModal();
      }
    },
    [closeNsmpModal, currentIndex, filteredRows, syncCarouselNsmpId],
  );

  const { savePayload } = useUsoMutuoSave({
    nsirStatus,
    digitadorId,
    userId,
    userRole,
    nsirId,
    digitacaoAntecipada: nsirData?.digitacaoAntecipada,
    digitarNsmpUsoMutuo,
    updateNsmp,
    saveNsmp,
    onSuccess: closeModalAfterSave,
    onReatribuida: (message) => {
      setReatribuidaDialog({ open: true, message });
    },
  });

  const handlerConfirm = (data, goNext) => {
    savePayload(data, { advanceNext: goNext });
  };

  const hasNext = currentIndex !== null && currentIndex < filteredRows.length - 1;
  const hasPrevious = currentIndex !== null && currentIndex > 0;
  const carouselPosition = currentIndex !== null
    ? { current: currentIndex + 1, total: filteredRows.length }
    : null;

  const getHeaderActions = () => {
    const actions = [];
    if (nsirStatus === Status.EM_INSPECAO) {
      actions.push({
        isComponent: true,
        component: (
          <ButtonMenuV2
            title="Novo"
            options={[
              { title: 'Poste', onClick: onAddPoste },
              { title: 'Entrega Sucata', onClick: onAddEntrega },
              { title: 'Pesagem Sucata', onClick: onAddPesagem },
            ]}
          />
        ),
      });
    }
    if (nsirStatus === Status.EM_INSPECAO
      && filteredRows.filter((row) => isAnaliseRiscoOrFotoRegistro(row)).length === 0) {
      actions.push({
        title: 'Análise de Risco',
        onClick: () => setOpenAnaliseRisco(true),
      });
    }
    return actions;
  };

  const renderRows = (row) => (
    <>
      <TableCell>{renderUsoMutuoItemCell(row)}</TableCell>
      <TableCell><UsoMutuoCoordenadaCell row={row} /></TableCell>
      <TableCell>{renderUsoMutuoSituacaoCell(row)}</TableCell>
      <TableCell>{renderUsoMutuoStatusCell(row)}</TableCell>
    </>
  );

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

  const getFotos = async (fotos) => {
    const fotosList = [];
    for (let i in fotos) {
      fotosList.push({
        id: uuidv4(),
        nome: fotos[i].name,
        image: await toBase64(fotos[i]),
      });
    }
    return fotosList;
  };

  const isInspetor = () => nsirStatus === Status.EM_INSPECAO && inspetorId === userId;
  const isDigitador = () => isDigitadorEmDigitacao({
    nsirStatus,
    digitadorId,
    userId,
    userRole,
    digitacaoAntecipada: nsirData?.digitacaoAntecipada,
  });

  const handleReatribuidaClose = () => {
    setReatribuidaDialog({ open: false, message: '' });
    closeNsmpModal();
    history.push('/v2/nsirs');
  };

  const canEditServiceRow = (row) =>
    isUsoMutuoServiceRow(row)
    && getUsoMutuoRowKind(row) !== USO_MUTUO_ROW_KIND.EVIDENCIA_CAMPO;

  const canViewDefault = isAdmin()
    || (nsirStatus !== Status.EM_INSPECAO
      && nsirStatus !== Status.EM_DIGITACAO
      && !(nsirStatus === Status.INSPECAO_FINALIZADA && authData.role === Role.DIGITADOR));

  const canViewServiceRow = (row) =>
    getUsoMutuoRowKind(row) === USO_MUTUO_ROW_KIND.EVIDENCIA_CAMPO || canViewDefault;

  const handleClearAllFilters = () => {
    setQuickFilter(EMPTY_USO_MUTUO_QUICK_FILTER);
  };

  return (
    <>
      <Box>
        <UsoMutuoQuickFilters
          value={quickFilter}
          onChange={setQuickFilter}
          filterActive={filterActive}
          totalCount={sortedNsmps.length}
          filteredCount={filteredRows.length}
        />
        <TableWidgetV2
          rows={filteredRows}
          titles={TABLE_TITLES}
          renderRows={renderRows}
          showEditRow={canEditServiceRow}
          hasRowActions
          onEdit={onEdit}
          onView={onView}
          onDelete={(id) => deleteNsmp(id)}
          showDelete={isInspetor()}
          showEdit={isInspetor() || isDigitador()}
          showView
          showViewRow={canViewServiceRow}
          hasAdd={false}
          extraHeaderActions={getHeaderActions()}
          onClearFilters={handleClearAllFilters}
          actions={[
            {
              key: 'delete',
              icon: <DeleteIcon />,
              show: (row) => row.idNsmpReference && isDigitador(),
              onClick: (id) => {
                setOpenDeleteClone(true);
                setCloneId(id);
              },
            },
          ]}
        />
      </Box>

      <NsmpModal
        open={open}
        onCancel={handlerCancel}
        onConfirm={handlerConfirm}
        selectedId={selectedId}
        createTipoNsmp={createTipoNsmp}
        readOnly={readOnly}
        nsirTitle={nsirTitle}
        onNext={goToNext}
        onPrevious={goToPrevious}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        carouselPosition={carouselPosition}
      />

      <DropzoneModalV2
        onClose={() => setOpenFotoInicial(false)}
        open={openFotoInicial}
        onSave={async (fotos) => {
          setOpenFotoInicial(false);
          handlerConfirm({
            fotos: await getFotos(fotos),
            codigo: '000',
            fotoInicio: true,
            fotoFim: false,
            analiseRisco: false,
            dataFotoDrone: moment().valueOf(),
          });
        }}
      />
      <DropzoneModalV2
        onClose={() => setOpenFotoFinal(false)}
        open={openFotoFinal}
        onSave={async (fotos) => {
          setOpenFotoFinal(false);
          handlerConfirm({
            fotos: await getFotos(fotos),
            codigo: '999',
            fotoInicio: false,
            fotoFim: true,
            analiseRisco: false,
            dataFotoDrone: moment().valueOf(),
          });
        }}
      />
      <DropzoneModalV2
        onClose={() => setOpenAnaliseRisco(false)}
        open={openAnaliseRisco}
        onSave={async (fotos) => {
          setOpenAnaliseRisco(false);
          handlerConfirm({
            fotos: await getFotos(fotos),
            codigo: '-001',
            fotoInicio: false,
            fotoFim: false,
            analiseRisco: true,
            dataFotoDrone: moment().valueOf(),
          });
        }}
      />

      <DialogDefaultV2
        open={openClone}
        handlerCancel={() => { setOpenClone(false); setCloneId(null); }}
        handlerConfirm={() => {
          cloneNsmp(cloneId, () => {
            setCloneId(null);
            setOpenClone(false);
          });
        }}
        title="Você tem certeza que deseja clonar o serviço??"
        confirmMessage="Clonar"
      >
        <DialogContentText>
          Ao clonar o serviço, todas as informações e fotos serão duplicadas. O novo serviço terá um novo código.
        </DialogContentText>
      </DialogDefaultV2>

      <DialogDefaultV2
        open={openDeleteClone}
        handlerCancel={() => { setOpenDeleteClone(false); setCloneId(null); }}
        handlerConfirm={() => {
          deleteCloneNsmp(cloneId, () => {
            setOpenDeleteClone(false);
            setCloneId(null);
          });
        }}
        title="Você tem que deseja remover o serviço?"
        confirmMessage="Remover"
      >
        <DialogContentText>
          A informação será removida e não poderá ser mais recuperado.
        </DialogContentText>
      </DialogDefaultV2>

      <ReatribuidaDialog
        open={reatribuidaDialog.open}
        message={reatribuidaDialog.message}
        onClose={handleReatribuidaClose}
      />

    </>
  );
};

UsoMutuoServiceTab.propTypes = {
  nsmps: PropTypes.array,
  nsirId: PropTypes.string,
  updateNsmp: PropTypes.func.isRequired,
  saveNsmp: PropTypes.func.isRequired,
  digitarNsmpUsoMutuo: PropTypes.func.isRequired,
  cleanSelectedNsmp: PropTypes.func.isRequired,
  nsirStatus: PropTypes.string,
  userRole: PropTypes.string,
  deleteNsmp: PropTypes.func.isRequired,
  authData: PropTypes.object,
  userId: PropTypes.string,
  inspetorId: PropTypes.string,
  digitadorId: PropTypes.string,
  nsirTitle: PropTypes.string,
  cloneNsmp: PropTypes.func.isRequired,
  deleteCloneNsmp: PropTypes.func.isRequired,
  history: PropTypes.object,
  location: PropTypes.object,
  nsirData: PropTypes.object,
};

const mapStateToProps = (state) => ({
  userRole: selectorsAuth.getRole(state),
  userId: selectorsAuth.getUserId(state),
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsNsmp }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(UsoMutuoServiceTab);
